export interface IdentityUser {
  id?: string;
  org_id?: string;
  role?: string;
}

export const IDENTITY_CLEAR = 'identity/clear';
export const IDENTITY_CHANGED = 'identity/changed';
export const SETUP_DRAFT_PREFIX = 'karla:agent-quick-setup:';
export const SOURCE_DRAFT_PREFIX = 'karla_quick_setup_source_draft_';
export const SETUP_RETURN_SCOPE_KEY = 'karla_quick_setup_identity_scope';
let generation = 0;
let scopeKey = '';
const requests = new Set<AbortController>();

export function getIdentityScopeKey(user: IdentityUser | null | undefined): string {
  return user?.id && user.org_id && user.role ? JSON.stringify([user.id, user.org_id, user.role]) : '';
}

export function getIdentityGeneration(): number {
  return generation;
}

export function registerIdentityRequest(controller: AbortController): () => void {
  requests.add(controller);
  return () => requests.delete(controller);
}

export function clearIdentityDrafts(previousScope: string): void {
  if (typeof window === 'undefined') return;
  try {
    const scopedPrefix = `${SETUP_DRAFT_PREFIX}${encodeURIComponent(previousScope)}:`;
    for (const key of Object.keys(window.localStorage)) {
      if (!key.startsWith(SETUP_DRAFT_PREFIX)) continue;
      // Old drafts used only a model ID and cannot establish identity ownership.
      const suffix = key.slice(SETUP_DRAFT_PREFIX.length);
      if (!suffix.startsWith('%5B') || (previousScope && key.startsWith(scopedPrefix))) window.localStorage.removeItem(key);
    }
  } catch {
    // Storage is best effort; inaccessible storage must never block a session reset.
  }
  try {
    const sourcePrefix = `${SOURCE_DRAFT_PREFIX}${encodeURIComponent(previousScope)}:`;
    for (const key of Object.keys(window.sessionStorage)) {
      if (!key.startsWith(SOURCE_DRAFT_PREFIX)) continue;
      const suffix = key.slice(SOURCE_DRAFT_PREFIX.length);
      if (!suffix.startsWith('%5B') || (previousScope && key.startsWith(sourcePrefix))) window.sessionStorage.removeItem(key);
    }
    const returnScope = window.sessionStorage.getItem(SETUP_RETURN_SCOPE_KEY);
    if (!returnScope || returnScope !== scopeKey || (previousScope && returnScope === previousScope)) {
      for (const key of [SETUP_RETURN_SCOPE_KEY, 'karla_quick_setup_integration_return_to', 'karla_quick_setup_integration_name']) window.sessionStorage.removeItem(key);
    }
  } catch {
    // Session storage follows the same identity boundary as persisted drafts.
  }
}

export function advanceIdentityScope(nextScope: string): number {
  const previousScope = scopeKey;
  scopeKey = nextScope;
  generation += 1;
  for (const controller of requests) controller.abort();
  requests.clear();
  clearIdentityDrafts(previousScope);
  return generation;
}

export function requestIdentityReset(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('karla:identity-reset'));
}
